import {
  ActivityType,
  BolusState,
  PumpSuspendState,
} from "@tandemdiabetes/sugarmate-api-react-client"
import classNames from "classnames"
import dayjs from "dayjs"
import {
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react"
import AutoSizer from "react-virtualized-auto-sizer"
import { VariableSizeList } from "react-window"

import { useTabContext } from "../TabContext"
import { FeedContext } from "./FeedContext"
import type { ActivityItem } from "./useSortedFeedItems"
import {
  useSortedFeedItems,
  ongoingSectionId,
} from "./useSortedFeedItems"
import styles from "./VirtualList.module.scss"
import { VirtualListItem } from "./VirtualListItem"
import { useFollowedUsers } from "src/contexts"
import { useRefValue } from "src/hooks"
import { Tab } from "src/types"
import type {
  ReactWindowAutoSizerArgs,
  ReactWindowOnItemsRenderedArgs,
  ReactWindowOnScrollArgs,
} from "src/types"

export interface Props {
  scrollToDate?: Date;
  setGraphScrollToDate: (v: Date) => void;
}

const defaultSize = 86

function isOngoingEvent(
  item: ActivityItem,
): boolean {
  const { activity } = item
  if ("event_type" in activity) {
    if (activity.event_type === ActivityType.Bolus) {
      return activity.bolus.state === BolusState.InProgress
    }

    if (activity.event_type === ActivityType.PumpSuspension) {
      return activity.pump_suspension.state === PumpSuspendState.InProgress
    }
  }

  return false
}

/*
 * each item in the virtual list represents all of the entries and events for a specific day
 */
export function VirtualList(props: Props) {
  const {
    scrollToDate,
    setGraphScrollToDate,
  } = props

  const { currentUserId } = useFollowedUsers()
  const { currentTab } = useTabContext()

  const listRef = useRef<VariableSizeList>(null)
  const itemSizesRef = useRef<Record<string, number>>({})
  const scrollPositionRef = useRef(0)
  const visibleIndicesRef = useRef<[number, number]>([
    1,
    1,
  ])

  const items = useSortedFeedItems(currentUserId ?? -1)
  const itemsRef = useRefValue(items)

  const getIndexId = useCallback(
    (index: number) => {
      const item = items[index]
      if (typeof item === "string") {
        return item
      }

      return item.activity.id
    },
    [items],
  )

  const getIndexItem = useCallback(
    (index: number) => items[index],
    [items],
  )

  const getIndexSize = useCallback(
    (index: number) => {
      const key = getIndexId(index)
      return itemSizesRef.current[key] ?? defaultSize
    },
    [
      itemSizesRef,
      getIndexId,
    ],
  )

  // when the item shown at the top of the feed changes scroll the graph to the same item
  const onItemsRendered = useCallback(
    (args: ReactWindowOnItemsRenderedArgs) => {
      visibleIndicesRef.current = [
        args.visibleStartIndex,
        args.visibleStopIndex,
      ]

      const item = itemsRef.current[args.visibleStartIndex]

      if (!item) {
        return
      }

      if (typeof item === "string") {
        if (item === ongoingSectionId) {
          setGraphScrollToDate(dayjs().toDate())
        } else {
          const isToday = dayjs().isSame(
            item,
            "day",
          )

          if (isToday) {
            setGraphScrollToDate(dayjs().toDate())
          } else {
            setGraphScrollToDate(
              dayjs(item)
                .endOf("day")
                .toDate(),
            )
          }
        }
      } else {
        setGraphScrollToDate(dayjs(item.activity.started_at).toDate())
      }
    },
    [
      visibleIndicesRef,
      setGraphScrollToDate,
      itemsRef,
    ],
  )

  // scroll to date when scrollToDate changes
  useEffect(
    () => {
      if (!listRef.current || !itemsRef.current) {
        return
      }

      let index: number | undefined = undefined

      for (let i = 0; i < itemsRef.current.length; i++) {
        const item = itemsRef.current[i]

        if (typeof item === "string") {
          if (item === ongoingSectionId) {
            // If the scrollToDate is Now, then scroll to the ongoing section
            if (dayjs().isBefore(
              scrollToDate,
              "minute",
            )) {
              index = i
              break
            }
          }

          if (
            dayjs(item).isSame(
              scrollToDate,
              "day",
            )
          ) {
            index = i
          } else if (
            dayjs(item).isBefore(
              scrollToDate,
              "day",
            )
          ) {
            if (index === undefined) {
              index = i
            }

            break
          }
        } else {
          if (isOngoingEvent(item)) {
            continue
          }
          if (dayjs(item.activity.started_at).isBefore(scrollToDate)) {
            if (index === undefined || i - index > 1) {
              index = i
            }

            break
          }
        }
      }

      if (index === undefined) {
        return
      }

      listRef.current.scrollToItem(
        index,
        "start",
      )
    },
    [
      scrollToDate,
      listRef,
      itemsRef,
    ],
  )

  const onScroll = useCallback(
    ({ scrollOffset }: ReactWindowOnScrollArgs) => {
      scrollPositionRef.current = scrollOffset
    },
    [scrollPositionRef],
  )

  const updateIndexSize = useCallback(
    (index: number, size: number) => {
      // update the value stored for this index's size
      const key = getIndexId(index)

      const previousSize = itemSizesRef.current[key]
      itemSizesRef.current[key] = size

      // if the size has not changed, then return
      if (previousSize === size) {
        return
      }

      const scrollPosition = scrollPositionRef.current
      const list = listRef.current

      // if there is no reference to the list, then return
      if (!list) {
        return
      }

      // tells list to update its layout
      list.resetAfterIndex(
        index,
        true,
      )

      // prevent the update from shifting the user's scroll position
      if (index < visibleIndicesRef.current[0]) {
        const sizeDiff = previousSize === undefined ? 0 : size - previousSize
        list.scrollTo(scrollPosition + sizeDiff)
      }
    },
    [
      listRef,
      itemSizesRef,
      getIndexId,
      visibleIndicesRef,
      scrollPositionRef,
    ],
  )

  // this ref callback is invoked immediately when the list is rendered
  // this tells the list to rerender so that we can account for the updated heights of the rendered items
  const updateList = useCallback(
    (node: VariableSizeList) => {
      if (node) {
        node.resetAfterIndex(
          0,
          true,
        )
      }
      listRef.current = node
    },
    // changing the value of `updateList` forces it to be re-executed when the
    // list changes if updateList is not re-executed when items change then
    // there will be issues when switching between users
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      listRef,
      items,
    ],
  )

  const className = classNames(
    styles.container,
    {
      [styles.containerHidden]: currentTab !== Tab.Feed,
    },
  )

  const contextValue = useMemo(
    () => ({
      getIndexItem,
      updateIndexSize,
    }),
    [
      getIndexItem,
      updateIndexSize,
    ],
  )

  return (
    <div
      className={className}
      id="scrollbar"
    >
      <FeedContext.Provider value={contextValue}>
        <AutoSizer>
          {({
            height,
            width,
          }: ReactWindowAutoSizerArgs) => (
            <div id="scrollbar-content">
              <VariableSizeList
                height={height}
                width={width}
                ref={updateList}
                itemCount={items.length}
                estimatedItemSize={defaultSize}
                layout="vertical"
                overscanCount={3}
                itemSize={getIndexSize}
                itemKey={getIndexId}
                onItemsRendered={onItemsRendered}
                onScroll={onScroll}
                className={styles.list}
              >
                {VirtualListItem}
              </VariableSizeList>
            </div>
          )}
        </AutoSizer>
      </FeedContext.Provider>
    </div>
  )
}
